import type { CourtMatch } from '../components/UploadTicket/UploadTicket';
import { shrinkImageForUpload } from '../utils/shrinkImageForUpload';
import { parseApiErrorMessage } from '../utils/parseApiError';

/**
 * "Create case" from a dropped, chosen or pasted document (backend: routes/CaseIntake). Nothing is
 * created by `read` or `match`; only `create`, which staff press after reviewing.
 */

/** What the document said. Every field may be null: absent means absent, never guessed. */
export interface CaseIntakeParsed {
  ticketNumber: string | null;
  citation: string | null;
  address: string | null;
  city: string | null;
  county: string | null;
  courtDate: string | null;
  courtName: string | null;
  respondBy: string | null;
  licenseType: string | null;
  issuingAgency: string | null;
  agencyCaseNumber: string | null;
  licensePlate: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  driversLicenseNumber: string | null;
  driversLicenseState: string | null;
  driversLicenseClass: 'CDL' | 'Regular' | null;
  clientAddress: string | null;
  clientCity: string | null;
  clientState: string | null;
  clientZip: string | null;
  phone: string | null;
  email: string | null;
  documentKind: 'citation' | 'referral-email' | 'web-page' | 'other';
  referralPartner: 'OTR' | null;
  referralEvidence: string | null;
  confidence: 'high' | 'medium' | 'low';
}

export type IntakeMatchReason = 'licence' | 'email' | 'phone' | 'name-dob' | 'name-only';

export interface IntakeClientCandidate {
  userGuid: string;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  email: string | null;
  phone: string | null;
  licences: string[];
  caseCount: number;
  lastCaseAt: string | null;
  reasons: IntakeMatchReason[];
  warnings: string[];
  preselectable: boolean;
}

export interface CaseWithCitation {
  Ticket_guid: string;
  Ticket_Number: string;
  Ticket_MatterNumber: string | null;
  Ticket_Active: boolean;
  clientName: string;
  User_guid: string;
}

export interface IntakeMatchResponse {
  candidates: IntakeClientCandidate[];
  /** A candidate's guid, 'new' when nobody was found, or null when staff must choose. */
  preselected: string | 'new' | null;
  duplicates: CaseWithCitation[];
  emailHeldBy: { User_guid: string; name: string } | null;
}

/** What the server answers on receiving a document: the read itself runs in the background. */
export interface IntakeReadStarted {
  token: string;
  fileName: string;
  mimeType: string;
  /** Set when the document is one already on the client's file. */
  existingDocumentGuid?: string;
}

/** A finished read. */
export interface IntakeReadResult extends IntakeMatchResponse {
  parsed: CaseIntakeParsed | null;
  fieldSources: string[];
  parseError: string | null;
  courtMatch: CourtMatch | null;
  /** How long the read took on the server, in milliseconds. */
  readMs?: number;
}

export type IntakeReadState =
  | { status: 'reading' }
  | { status: 'failed'; error: string }
  | ({ status: 'done' } & IntakeReadResult);

export interface IntakeCreateRequest {
  /** A dropped document: its staging token and name. */
  token?: string;
  fileName?: string;
  /** Or one already on the client's file. */
  existingDocumentGuid?: string;
  documentClientVisible: boolean;
  /** Record this document as the case's citation. */
  documentIsCitation: boolean;
  /** A new client's source, as on the create-client form. */
  User_Source?: string;
  User_guid?: string;
  User_FirstName?: string;
  User_MiddleName?: string;
  User_LastName?: string;
  User_DOB?: string;
  User_Email?: string;
  User_PhoneNumber?: string;
  User_DriversLicenseNumber?: string;
  User_LicenseType?: string;
  Ticket_Number: string;
  Ticket_Citation: string;
  Ticket_Address: string;
  Ticket_City: string;
  Ticket_County: string;
  Ticket_CourtDate: string;
  Ticket_RespondByDate: string;
  Ticket_LicenseType: string;
  Ticket_Zone: string;
  Ticket_Representation: string;
  Ticket_ArrestingAgency?: string;
  Ticket_AgencyCaseNumber?: string;
  Ticket_LicensePlate?: string;
  Ticket_Gender?: string;
  Ticket_PronounSet?: string;
  Ticket_HasTakenDscInPastYear: boolean;
  Court_guid: string;
  Prosecutor_guid?: string;
  markOtr: boolean;
  createDespiteDuplicate: boolean;
  clientAddress?: string;
}

export interface IntakeCreateResponse {
  ticketGuid: string;
  userGuid: string;
  clientCreated: boolean;
  emailNotSaved: string | null;
}

/** What POST /case-intake/payment-defaults answers (payments/paymentStepDefaults on the server). */
export interface IntakePaymentDefaults {
  mode: 'ready' | 'partner' | 'blocked';
  note: string | null;
  email: boolean;
  sms: boolean;
  emailUnavailable: string | null;
  smsUnavailable: string | null;
  emailAddress: string | null;
  phoneNumber: string | null;
  planCents: number | null;
}

function apiUrl(path: string): string {
  const apiHost = process.env.API_HOST?.trim() || window.location.origin;
  return `${apiHost}/api/v1/case-intake${path}`;
}

async function readJson<T>(response: Response, fallback: string): Promise<T> {
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(parseApiErrorMessage(body, fallback));
  }
  return body as T;
}

class CaseIntakeApi {
  /** Upload a file, or pasted text, and start reading it. Answers at once. */
  static async read(input: { file: File } | { text: string }): Promise<IntakeReadStarted> {
    const form = new FormData();
    if ('file' in input) {
      // A large phone photo is redrawn smaller first, so it is neither refused nor too big to read.
      form.append('file', await shrinkImageForUpload(input.file));
    } else {
      form.append('text', input.text);
    }
    const response = await fetch(apiUrl('/read'), {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    return readJson(
      response,
      response.status === 413
        ? 'This file is larger than 50 MB. For a photo, take it again at a lower resolution or upload a screenshot of it. For a PDF, save only the pages you need.'
        : `Upload failed (${response.status})`,
    );
  }

  /** Start reading a document already on a client's file. Nothing is uploaded or copied. */
  static async readDocument(userGuid: string, documentGuid: string): Promise<IntakeReadStarted> {
    const response = await fetch(apiUrl('/read-document'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ User_guid: userGuid, UserDocument_guid: documentGuid }),
    });
    return readJson(response, 'That document could not be opened.');
  }

  /** Where a read has got to. */
  static async readResult(token: string): Promise<IntakeReadState> {
    const response = await fetch(apiUrl(`/read/${encodeURIComponent(token)}`), {
      credentials: 'include',
    });
    return readJson(response, 'Could not check on the document.');
  }

  /**
   * How "Send payment link by email and text" opens on the review screen, before the case exists:
   * ticked for a client who pays the firm directly, unticked for a partner's, refused for OTR.
   */
  static async paymentDefaults(input: {
    User_guid?: string;
    source?: string;
    email?: string;
    phone?: string;
    markOtr: boolean;
    plan?: string;
  }): Promise<IntakePaymentDefaults> {
    const response = await fetch(apiUrl('/payment-defaults'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(input),
    });
    return readJson(response, 'Could not work out how to send the payment link.');
  }

  /** Re-run the client match and duplicate check after staff correct a field. */
  static async match(identity: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    driversLicenseNumber: string;
    email: string;
    phone: string;
    ticketNumber: string;
  }): Promise<IntakeMatchResponse> {
    const response = await fetch(apiUrl('/match'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(identity),
    });
    return readJson(response, 'Could not look up matching clients.');
  }

  static async create(request: IntakeCreateRequest): Promise<IntakeCreateResponse> {
    const response = await fetch(apiUrl('/create'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(request),
    });
    return readJson(response, 'The case could not be created. Please try again.');
  }
}

export default CaseIntakeApi;
